import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Download, Mail, CheckCircle, UserPlus, ShieldCheck, AlertTriangle, Flag, Edit, Camera, FileText, LogIn, RefreshCw, Activity, Clock, Truck, Check } from 'lucide-react';

const ICON_MAP = {
  'mail': Mail, 'check-circle': CheckCircle, 'user-plus': UserPlus,
  'shield-check': ShieldCheck, 'alert-triangle': AlertTriangle, 'flag': Flag,
  'edit': Edit, 'camera': Camera, 'file-text': FileText, 'log-in': LogIn,
  'refresh-cw': RefreshCw, 'activity': Activity, 'plus-circle': CheckCircle,
  'check': Check, 'check-square': CheckCircle, 'alert-circle': AlertTriangle,
  'truck': Truck,
};

const TYPE_COLORS = {
  'create': 'bg-blue-100 text-blue-700 border-blue-300',
  'invitation_sent': 'bg-indigo-100 text-indigo-700 border-indigo-300',
  'submit': 'bg-green-100 text-green-700 border-green-300',
  'candidate_submitted': 'bg-green-100 text-green-700 border-green-300',
  'approve': 'bg-emerald-100 text-emerald-700 border-emerald-300',
  'qc_approved': 'bg-emerald-100 text-emerald-700 border-emerald-300',
  'assign': 'bg-purple-100 text-purple-700 border-purple-300',
  'check_assigned_agent': 'bg-purple-100 text-purple-700 border-purple-300',
  'check_assigned_vendor': 'bg-violet-100 text-violet-700 border-violet-300',
  'insufficiency': 'bg-amber-100 text-amber-700 border-amber-300',
  'insufficiency_raised': 'bg-amber-100 text-amber-700 border-amber-300',
  'insufficiency_cleared': 'bg-teal-100 text-teal-700 border-teal-300',
  'clear': 'bg-teal-100 text-teal-700 border-teal-300',
  'complete': 'bg-green-100 text-green-700 border-green-300',
  'check_status_updated': 'bg-green-100 text-green-700 border-green-300',
  'field_verification_submitted': 'bg-cyan-100 text-cyan-700 border-cyan-300',
  'case_status_updated': 'bg-blue-100 text-blue-700 border-blue-300',
  'data_edited': 'bg-orange-100 text-orange-700 border-orange-300',
  'candidate_login': 'bg-slate-100 text-slate-700 border-slate-300',
};

function formatTimestamp(ts) {
  if (!ts) return '-';
  try {
    const d = new Date(ts);
    return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
  } catch { return ts.slice(0, 16); }
}

export default function AuditTrail({ caseId }) {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!caseId) return;
    fetchAuditTrail();
  }, [caseId]);

  const fetchAuditTrail = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/cases/${caseId}/activity-log`);
      setActivities(res.data.activities || []);
    } catch (err) {
      toast.error('Failed to load audit trail');
    } finally {
      setLoading(false);
    }
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      const response = await axios.post(`/cases/${caseId}/audit-trail/export-pdf`, {}, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Audit_Trail_${caseId}_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Audit trail PDF downloaded');
    } catch {
      toast.error('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3" />
        <span className="text-sm text-slate-500">Loading audit trail...</span>
      </div>
    );
  }

  return (
    <div data-testid="audit-trail-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-slate-600" />
          <h3 className="text-base font-semibold text-slate-800">Audit Trail</h3>
          <Badge variant="secondary" className="text-xs">{activities.length} events</Badge>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchAuditTrail} data-testid="refresh-audit-trail">
            <RefreshCw className="w-3.5 h-3.5 mr-1" />Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={exportPdf} disabled={exporting} data-testid="export-audit-pdf">
            <Download className="w-3.5 h-3.5 mr-1" />{exporting ? 'Exporting...' : 'Export PDF'}
          </Button>
        </div>
      </div>

      {activities.length === 0 ? (
        <div className="text-center py-8 text-slate-400 text-sm">No audit events recorded yet.</div>
      ) : (
        <div className="relative">
          <div className="absolute left-5 top-0 bottom-0 w-px bg-slate-200" />
          <div className="space-y-0">
            {activities.map((act, idx) => {
              const IconComp = ICON_MAP[act.icon] || Activity;
              const colorClass = TYPE_COLORS[act.action_type] || 'bg-slate-100 text-slate-600 border-slate-300';
              return (
                <div key={act.id || idx} className="relative flex items-start gap-3 py-2.5 pl-2" data-testid={`audit-event-${idx}`}>
                  <div className={`relative z-10 flex items-center justify-center w-7 h-7 rounded-full border ${colorClass} shrink-0`}>
                    <IconComp className="w-3.5 h-3.5" />
                  </div>
                  <div className="flex-1 min-w-0 pt-0.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-slate-800">{act.action}</span>
                      {act.check_type && (
                        <Badge variant="outline" className="text-[10px] py-0 px-1.5">{act.check_type.replace('_', ' ')}</Badge>
                      )}
                    </div>
                    {act.description && (
                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{act.description}</p>
                    )}
                    <div className="flex items-center gap-3 mt-1 text-[11px] text-slate-400">
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatTimestamp(act.timestamp)}</span>
                      <span>by {act.user || 'System'}</span>
                      {act.user_role && act.user_role !== 'system' && (
                        <Badge variant="secondary" className="text-[9px] py-0 px-1">{act.user_role.replace('_', ' ')}</Badge>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
